import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Send, Loader2, Check, FileText, PhoneCall } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, formatApiErrorDetail, trackEvent } from "../lib/api";
import { COMPANY } from "../lib/data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", code_postal: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Merci d'accepter la politique de confidentialité."); return; }
    setLoading(true);
    try {
      await api.post("/quote", { ...form, type_demande: "Contact / Autre", consentement: consent });
      trackEvent("form_submit", "Message contact envoyé");
      setDone(true);
      toast.success("Message envoyé, merci !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact — Carnier Toiture et Habitat (Oise) | 07 69 53 51 60"
        description="Contactez Carnier Toiture et Habitat pour vos travaux de toiture, façade et habitat dans l'Oise, l'Île-de-France et la Picardie. Téléphone : 07 69 53 51 60."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">Contact</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance">Parlons de votre projet</h1>
            <p className="mt-4 max-w-2xl text-vegetal-100/80 text-lg">Une question, un conseil, un chiffrage ? Nous sommes à votre écoute.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-2 gap-10">
        <Reveal>
          <div className="space-y-4">
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-4 rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 hover:border-terracotta-300 transition-colors">
              <div className="grid place-items-center h-12 w-12 rounded-lg bg-terracotta-50 text-terracotta-600"><Phone className="h-6 w-6" /></div>
              <div><p className="text-sm text-neutral-500">Téléphone</p><p className="font-heading font-bold text-xl text-neutral-900">{COMPANY.phone}</p></div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-neutral-200 shadow-sm p-6">
              <div className="grid place-items-center h-12 w-12 rounded-lg bg-vegetal-50 text-vegetal-600"><MapPin className="h-6 w-6" /></div>
              <div><p className="text-sm text-neutral-500">Zone d'intervention</p><p className="font-medium text-neutral-900">Oise (60) — Île-de-France & Picardie</p></div>
            </div>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 hover:border-terracotta-300 transition-colors">
              <div className="grid place-items-center h-12 w-12 rounded-lg bg-neutral-100 text-neutral-500"><Mail className="h-6 w-6" /></div>
              <div><p className="text-sm text-neutral-500">Email</p><p className="font-medium text-neutral-900 break-all">{COMPANY.email}</p></div>
            </a>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Link to="/devis" className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium"><FileText className="h-4 w-4" /> Devis</Link>
              <Link to="/rappel" className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-md border border-neutral-200 text-neutral-800 hover:border-terracotta-400 transition-colors font-medium"><PhoneCall className="h-4 w-4" /> Rappel</Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          {done ? (
            <div className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-8 text-center h-full grid place-items-center">
              <div>
                <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-vegetal-100 text-vegetal-600"><Check className="h-7 w-7" /></div>
                <h2 className="mt-5 font-heading text-2xl font-bold text-neutral-900">Message envoyé !</h2>
                <p className="mt-3 text-neutral-700">Nous vous répondons au plus vite.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label className="text-sm font-medium text-neutral-800">Nom <span className="text-terracotta-500">*</span></Label><Input data-testid="contact-nom" required value={form.nom} onChange={set("nom")} className="mt-1.5" /></div>
                <div><Label className="text-sm font-medium text-neutral-800">Téléphone <span className="text-terracotta-500">*</span></Label><Input data-testid="contact-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} className="mt-1.5" /></div>
                <div><Label className="text-sm font-medium text-neutral-800">Email <span className="text-terracotta-500">*</span></Label><Input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
                <div><Label className="text-sm font-medium text-neutral-800">Code postal <span className="text-terracotta-500">*</span></Label><Input data-testid="contact-cp" required value={form.code_postal} onChange={set("code_postal")} className="mt-1.5" /></div>
              </div>
              <div><Label className="text-sm font-medium text-neutral-800">Message <span className="text-terracotta-500">*</span></Label><Textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Votre message..." /></div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="contact-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                <span className="text-xs text-neutral-600 leading-relaxed">J'accepte que mes données soient utilisées pour répondre à ma demande. Voir la <a href="/politique-confidentialite" className="text-terracotta-600 underline">politique de confidentialité</a>.</span>
              </label>
              <button data-testid="contact-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : <><Send className="h-4 w-4" /> Envoyer le message</>}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
